import { EventEmitter, Injectable, OnDestroy } from '@angular/core';
import { CompletionMessage } from '@app-libs/model';
import { Observable, Subscription } from 'rxjs';
import { EventBusService, EventTypes } from '../client/event-bus.service';
import { ChatStateService } from './chat-state.service';
import { ChatOrchestrator } from './core/chat-orchestrator.service';
import { ChatType } from './core/chat.types';

/**
 * ChatService - Clean Facade/Delegation pattern for ChatOrchestrator
 *
 * This service provides a focused interface for chat operations,
 * delegating core functionality to the ChatOrchestrator.
 *
 * Phase 6 Cleanup Applied:
 * - Removed 6 unused methods (updateProcessingFlag, isConversationLoad, strip_citations, etc.)
 * - Removed 7 unused observable properties (selectedModel$, workspaceId$, etc.)
 * - Simplified state getter to only include actively used properties
 * - Maintained 100% backwards compatibility for component usage
 */
@Injectable({
  providedIn: 'root',
})
export class ChatService implements OnDestroy {
  /**
   * Event emitter for scrolling the chat window to the bottom.
   */
  scrollToBottom: EventEmitter<void> = new EventEmitter<void>();
  private subscriptions: Subscription = new Subscription();

  // Core observable access for component usage
  public readonly state$: any;
  public readonly messages$: any;
  public readonly processing$: any;
  public readonly assistant$: any;

  constructor(
    private chatStateService: ChatStateService,
    private eventBus: EventBusService,
    private chatOrchestrator: ChatOrchestrator,
  ) {
    console.debug('ChatService constructor - delegating to ChatOrchestrator');

    // Initialize core observables used by components
    this.state$ = this.chatStateService.state$;
    this.messages$ = this.chatStateService.messages$;
    this.processing$ = this.chatStateService.processing$;
    this.assistant$ = this.chatStateService.assistant$;

    this.chatStateService.resetState();
    this.setupEventListeners();
  }

  // State getter for component usage - includes only actively used properties
  get state() {
    return {
      ...this.chatStateService.currentState,
      // Include observables for template compatibility
      processing$: this.processing$,
      modelSelectorEnabled$: this.chatStateService.modelSelectorEnabled$,
      enableAutoScrolling$: this.chatStateService.enableAutoScrolling$,
    };
  }

  private setupEventListeners() {
    this.subscriptions.add(
      this.eventBus.on(EventTypes.UPDATE_SELECTED_MODEL).subscribe((event) => {
        this.onUpdateSelectedModel(event.data);
      }),
    );
  }

  /**
   * Resets the entire chat back to its default state.
   */
  resetChat(): void {
    this.chatOrchestrator.resetChat();
  }

  /**
   * Initializes a chat session based on the provided chat type and ID.
   * @param chatType The type of chat to initialize (e.g., assistant, conversation).
   * @param id The unique ID of the chat session (e.g., assistant ID, conversation ID).
   * @returns A promise that resolves when the chat is fully initialized.
   */
  async initChat(chatType: ChatType, id?: string): Promise<void> {
    // Delegate to ChatOrchestrator with exact same interface
    await this.chatOrchestrator.initChat({ chatType, id });
  }

  /**
   * Sends a user's message and sets up a stream subscription for the assistant's response.
   * @param message The user's Message object (containing content, role, etc.).
   * @param file Optionally, a file upload associated with this message.
   */
  sendMessageStream(message: CompletionMessage): Observable<CompletionMessage> {
    // Delegate to ChatOrchestrator - exact same interface and behavior
    return this.chatOrchestrator.sendMessageStream(message);
  }

  // Note: Stream handling methods removed - now handled by ChatOrchestrator

  /**
   * Stops the current stream.
   */
  stopStream(): void {
    // Delegate to ChatOrchestrator
    this.chatOrchestrator.stopStream();
  }

  // Note: Stream creation methods removed - now handled by ChatOrchestrator


  removeUploadedFile(): boolean {
    // Delegate to ChatOrchestrator
    return this.chatOrchestrator.removeUploadedFile();
  }

  onUpdateSelectedModel(model: string): void {
    // Delegate to ChatOrchestrator
    this.chatOrchestrator.onUpdateSelectedModel(model);
  }

  transcribeAudio(audioBlob: Blob): Promise<string> {
    // Delegate to ChatOrchestrator
    return this.chatOrchestrator.transcribeAudio(audioBlob);
  }

  // Methods for components to use instead of direct state access
  setProcessing(processing: boolean): void {
    // Delegate to ChatOrchestrator
    this.chatOrchestrator.setProcessing(processing);
  }

  getUploadedFile(): any {
    // Delegate to ChatOrchestrator
    return this.chatOrchestrator.getUploadedFile();
  }

  setUploadedFile(file: any): void {
    // Delegate to ChatOrchestrator
    this.chatOrchestrator.setUploadedFile(file);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

}
